import React, { useContext, useEffect, useRef } from 'react';

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import { useAppSelector } from 'state/hooks';
import { InternalBoundaryPolygon } from 'state/userAdminSlices';
import { selectMapStyle } from 'state/workflowSlice';

import { MapContext } from 'features/map/MapContext';
import { initMap } from 'features/map/utils/mapCommon';
import { fitBoundsToBbox, isGeometryOnMap } from 'features/map/utils/mapUtils';
import { initZoom, safeCenter } from 'features/map/utils/map_constants';
import {
  isGeomGood,
  useMapSubscriptionBoundary,
} from 'features/user_admin/adminMapCommon';
import { Feature, MultiPolygon, Polygon } from 'geojson';

import styles from 'features/user_admin/NewUserProjectRequestPage.module.css';

export function ProjectBoundaryViewMap({
  boundary,
  subscriptionBoundary,
}: {
  boundary?: InternalBoundaryPolygon;
  subscriptionBoundary: Feature<Polygon | MultiPolygon> | undefined;
}) {
  const refMapContainer = useRef(null); // DOM node reference
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const refDraw = useRef(undefined);

  const mapStyle = useAppSelector(selectMapStyle);

  useMapSubscriptionBoundary(subscriptionBoundary, boundary);

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  useEffect(() => {
    const unmount = () => {
      // console.log('Unmount map');
      setMapReady(false);
      refStyleReady.current = false;
      // Wipe old map
      if (refMap.current) {
        refMap.current.remove();
        refMap.current = undefined;
        refDraw.current = undefined;
      }
    };
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
      refDraw.current = undefined;
    }
    // Init new map

    const center = { ...safeCenter };
    if (subscriptionBoundary) {
      const sub_center = turf.centroid(subscriptionBoundary);
      // eslint-disable-next-line prefer-destructuring
      center.lat = sub_center.geometry.coordinates[1];
      // eslint-disable-next-line prefer-destructuring
      center.lng = sub_center.geometry.coordinates[0];
    }

    initMap(refMap, refMapContainer, center, initZoom, mapStyle, false, {
      handleMapLoadCallback,
    });
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      // Select which mapbox-gl-draw control buttons to add to the map.
      controls: {
        polygon: false,
        trash: false,
      },
      modes: {
        static: {
          onSetup() {
            (this as any).setActionableState();
          },
          toDisplayFeatures(state, geojson, display) {
            display(geojson);
          },
        },
      },
      // Set mapbox-gl-draw to draw by default.
      // The user does not have to click the polygon control button first.
      defaultMode: 'static',
    });
    refMap.current.addControl(draw);
    refDraw.current = draw;

    if (boundary) {
      fitBoundsToBbox(refMap.current, boundary, 0.1);
    } else if (subscriptionBoundary) {
      fitBoundsToBbox(refMap.current, subscriptionBoundary.geometry, 0.1);
    }

    return unmount;
  }, [mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapReady) {
      if (isGeomGood(boundary)) {
        // console.log('Redraw boundary', boundary);
        refDraw.current.deleteAll();
        refDraw.current.add(boundary);

        if (!isGeometryOnMap(boundary, refMap.current)) {
          fitBoundsToBbox(refMap.current, boundary, 0.1);
        }
      } else if (!boundary) {
        refDraw.current.deleteAll();
      }
    }
  }, [boundary, mapReady]);

  return (
    <div ref={refMapContainer} className={styles.boundary_map_container} />
  );
}
